/*
|--------------------------------------------------------------------------
| Data Type Schemas
|--------------------------------------------------------------------------
|
| Joi validation schemas for Resourceful data types. These schemas ensure
| that data type options conform to OpenAPI 3.0 specifications and provide
| runtime validation for the data type system.
|
*/

import { joi } from '@nhtio/lucid-resourceful/joi'
import type {
  ResourcefulStringTypeOptions,
  ResourcefulDateTypeOptions,
  ResourcefulDateTimeTypeOptions,
  ResourcefulBinaryTypeOptions,
  ResourcefulNumberTypeOptions,
  ResourcefulIntegerTypeOptions,
  ResourcefulBigintTypeOptions,
  ResourcefulUnsignedIntegerTypeOptions,
  ResourcefulBooleanTypeOptions,
  ResourcefulObjectTypeOptions,
  ResourcefulArrayTypeOptions,
  ResourcefulEnumObject,
} from './data_types'

/*
|--------------------------------------------------------------------------
| Reusable Schema Components
|--------------------------------------------------------------------------
|
| These sub-schemas can be combined to create the main data type schemas
| while maintaining strict validation and supporting OpenAPI patterns.
|
*/

/** Common modifier properties that can be applied to any data type */
const CommonModifiersSchema = {
  nullable: joi.boolean().optional(),
  readOnly: joi.boolean().optional(),
  writeOnly: joi.boolean().optional(),
}

/** Base schema for a complete ResourcefulDataType instance (with type and modifiers) */
const ResourcefulDataTypeInstanceSchema = joi
  .alternatives()
  .try(
    // String types
    joi
      .object({
        type: joi.string().valid('string').required(),
        minLength: joi.number().min(0).default(0),
        maxLength: joi.number().min(0).default(Number.MAX_SAFE_INTEGER),
        pattern: joi
          .string()
          .custom((value, helpers) => {
            try {
              new RegExp(value)
              return value
            } catch (error) {
              /* istanbul ignore next */
              return helpers.error('any.invalid', { value })
            }
          }, 'regex validation')
          .allow('')
          .optional(),
        format: joi.string().disallow('date', 'date-time', 'binary').optional(),
        enum: joi
          .array()
          .items(
            joi.alternatives(
              joi.string(),
              joi.object<ResourcefulEnumObject<string>>({
                value: joi.string().required(),
                title: joi.string().optional().allow('', null),
                i18n: joi.boolean().default(false),
              })
            )
          )
          .optional(),
      })
      .append(CommonModifiersSchema),
    // Date types
    joi
      .object({
        type: joi.string().valid('string').required(),
        format: joi.string().valid('date').required(),
        autoCreate: joi.boolean().default(false),
        autoUpdate: joi.boolean().default(false),
      })
      .append(CommonModifiersSchema),
    // DateTime types
    joi
      .object({
        type: joi.string().valid('string').required(),
        format: joi.string().valid('date-time').required(),
        autoCreate: joi.boolean().default(false),
        autoUpdate: joi.boolean().default(false),
      })
      .append(CommonModifiersSchema),
    // Binary types
    joi
      .object({
        type: joi.string().valid('string').required(),
        format: joi.string().valid('binary').required(),
        minLength: joi.number().min(0).default(0),
        maxLength: joi.number().min(0).default(Number.MAX_SAFE_INTEGER),
      })
      .append(CommonModifiersSchema),
    // Number types
    joi
      .object({
        type: joi.string().valid('number').required(),
        minimum: joi.number().optional(),
        exclusiveMinimum: joi.boolean().default(false),
        maximum: joi.number().optional(),
        exclusiveMaximum: joi.boolean().default(false),
        format: joi.string().valid('float', 'double').optional(),
        multipleOf: joi.number().min(0).default(1),
      })
      .append(CommonModifiersSchema),
    // Integer types
    joi
      .object({
        type: joi.string().valid('integer').required(),
        minimum: joi.number().optional(),
        exclusiveMinimum: joi.boolean().default(false),
        maximum: joi.number().optional(),
        exclusiveMaximum: joi.boolean().default(false),
        format: joi.string().valid('int32', 'int64').optional(),
        multipleOf: joi.number().min(1).default(1),
      })
      .append(CommonModifiersSchema),
    // Boolean types
    joi
      .object({
        type: joi.string().valid('boolean').required(),
      })
      .append(CommonModifiersSchema),
    // Object types
    joi
      .object({
        type: joi.string().valid('object').required(),
        properties: joi
          .object()
          .pattern(joi.string(), joi.link('#resourcefulDataType'))
          .default({}),
        required: joi
          .array()
          .items(joi.string())
          .unique()
          .custom((value, helpers) => {
            /* istanbul ignore next */
            const properties = helpers.state.ancestors[0]?.properties || {}
            const propertyKeys = Object.keys(properties)

            // Check that all required keys exist in properties
            const invalidKeys = value.filter((key: string) => !propertyKeys.includes(key))
            /* istanbul ignore next 5 */
            if (invalidKeys.length > 0) {
              return helpers.error('any.custom', {
                message: `Required keys [${invalidKeys.join(', ')}] are not defined in properties`,
              })
            }

            return value
          }, 'required keys validation')
          .optional(),
        additionalProperties: joi
          .alternatives(joi.boolean(), joi.link('#resourcefulDataType'))
          .optional(),
        minProperties: joi.number().min(0).default(0),
        maxProperties: joi.number().min(0).default(Number.MAX_SAFE_INTEGER),
      })
      .append(CommonModifiersSchema),
    // Array types
    joi
      .object({
        type: joi.string().valid('array').required(),
        items: joi.link('#resourcefulDataType').required(),
        minItems: joi.number().min(0).default(0),
        maxItems: joi.number().min(0).default(Number.MAX_SAFE_INTEGER),
        uniqueItems: joi.boolean().default(false),
      })
      .append(CommonModifiersSchema),
    // Schema composition types
    joi
      .object({
        oneOf: joi.array().items(joi.link('#resourcefulDataType')).min(1).required(),
      })
      .append(CommonModifiersSchema),
    joi
      .object({
        allOf: joi.array().items(joi.link('#resourcefulDataType')).min(1).required(),
      })
      .append(CommonModifiersSchema),
    joi
      .object({
        anyOf: joi.array().items(joi.link('#resourcefulDataType')).min(1).required(),
      })
      .append(CommonModifiersSchema),
    joi
      .object({
        not: joi.array().items(joi.link('#resourcefulDataType')).min(1).required(),
      })
      .append(CommonModifiersSchema)
  )
  .id('resourcefulDataType')

/*
|--------------------------------------------------------------------------
| Individual Data Type Schemas
|--------------------------------------------------------------------------
|
| Schemas for each specific data type with their individual validation rules
|
*/

export const ResourcefulStringTypeSchema = () =>
  joi.object<ResourcefulStringTypeOptions>({
    minLength: joi.number().min(0).default(0),
    maxLength: joi.number().min(0).default(Number.MAX_SAFE_INTEGER),
    pattern: joi
      .string()
      .custom((value, helpers) => {
        try {
          new RegExp(value)
          return value
        } catch (error) {
          return helpers.error('any.invalid', { value })
        }
      }, 'regex validation')
      .allow('')
      .optional(),
    format: joi.string().disallow('date', 'date-time', 'binary').optional(),
    enum: joi
      .array()
      .items(
        joi.alternatives(
          joi.string(),
          joi.object<ResourcefulEnumObject<string>>({
            value: joi.string().required(),
            title: joi.string().optional().allow('', null),
            i18n: joi.boolean().default(false),
          })
        )
      )
      .optional(),
    ...CommonModifiersSchema,
  })

export const ResourcefulDateTypeSchema = () =>
  joi.object<ResourcefulDateTypeOptions>({
    ...CommonModifiersSchema,
    autoCreate: joi.boolean().default(false),
    autoUpdate: joi.boolean().default(false),
  })

export const ResourcefulDateTimeTypeSchema = () =>
  joi.object<ResourcefulDateTimeTypeOptions>({
    ...CommonModifiersSchema,
    autoCreate: joi.boolean().default(false),
    autoUpdate: joi.boolean().default(false),
  })

export const ResourcefulBinaryTypeSchema = () =>
  joi.object<ResourcefulBinaryTypeOptions>({
    minLength: joi.number().min(0).default(0),
    maxLength: joi.number().min(0).default(Number.MAX_SAFE_INTEGER),
    ...CommonModifiersSchema,
  })

export const ResourcefulNumberTypeSchema = () =>
  joi.object<ResourcefulNumberTypeOptions>({
    minimum: joi.number().default(0),
    exclusiveMinimum: joi.boolean().default(false),
    maximum: joi.number().default(Number.MAX_SAFE_INTEGER),
    exclusiveMaximum: joi.boolean().default(false),
    format: joi.string().valid('float', 'double').optional(),
    multipleOf: joi.number().min(0).default(1),
    ...CommonModifiersSchema,
  })

export const ResourcefulIntegerTypeSchema = () =>
  joi.object<ResourcefulIntegerTypeOptions>({
    minimum: joi.number().default(0),
    exclusiveMinimum: joi.boolean().default(false),
    maximum: joi.number().default(Number.MAX_SAFE_INTEGER),
    exclusiveMaximum: joi.boolean().default(false),
    multipleOf: joi.number().min(1).default(1),
    ...CommonModifiersSchema,
  })

export const ResourcefulBigintTypeSchema = () =>
  joi.object<ResourcefulBigintTypeOptions>({
    minimum: joi.alternatives(joi.number().default(0), joi.bigint().default(BigInt(0) as any)),
    exclusiveMinimum: joi.boolean().default(false),
    maximum: joi.alternatives(
      joi.number().default(Number.MAX_SAFE_INTEGER),
      joi.bigint().optional()
    ),
    exclusiveMaximum: joi.boolean().default(false),
    multipleOf: joi
      .alternatives(joi.number().min(1), joi.bigint().min(0n))
      .default(BigInt(1) as any),
    ...CommonModifiersSchema,
  })

export const ResourcefulUnsignedIntegerTypeSchema = () =>
  joi.object<ResourcefulUnsignedIntegerTypeOptions>({
    minimum: joi.number().min(0).default(0),
    exclusiveMinimum: joi.boolean().default(false),
    maximum: joi.number().default(Number.MAX_SAFE_INTEGER),
    exclusiveMaximum: joi.boolean().default(false),
    multipleOf: joi.number().min(1).default(1),
    ...CommonModifiersSchema,
  })

export const ResourcefulBooleanTypeSchema = () =>
  joi.object<ResourcefulBooleanTypeOptions>({
    ...CommonModifiersSchema,
  })

export const ResourcefulObjectTypeSchema = () =>
  joi.object<ResourcefulObjectTypeOptions>({
    properties: joi.object().pattern(joi.string(), ResourcefulDataTypeInstanceSchema).default({}),
    required: joi
      .array()
      .items(joi.string())
      .unique()
      .custom((value, helpers) => {
        /* istanbul ignore next */
        const properties = helpers.state.ancestors[0]?.properties || {}
        const propertyKeys = Object.keys(properties)

        // Check that all required keys exist in properties
        const invalidKeys = value.filter((key: string) => !propertyKeys.includes(key))
        /* istanbul ignore next 5 */
        if (invalidKeys.length > 0) {
          return helpers.error('any.custom', {
            message: `Required keys [${invalidKeys.join(', ')}] are not defined in properties`,
          })
        }

        return value
      }, 'required keys validation')
      .optional(),
    additionalProperties: joi
      .alternatives(joi.boolean(), ResourcefulDataTypeInstanceSchema)
      .optional(),
    minProperties: joi.number().min(0).default(0).optional(),
    maxProperties: joi.number().min(0).default(Number.MAX_SAFE_INTEGER).optional(),
    ...CommonModifiersSchema,
  })

export const ResourcefulArrayTypeSchema = () =>
  joi.object<ResourcefulArrayTypeOptions>({
    items: joi
      .alternatives()
      .try(
        ResourcefulDataTypeInstanceSchema,
        joi.object({
          oneOf: joi.array().items(ResourcefulDataTypeInstanceSchema).min(1).required(),
        }),
        joi.object({
          allOf: joi.array().items(ResourcefulDataTypeInstanceSchema).min(1).required(),
        }),
        joi.object({
          anyOf: joi.array().items(ResourcefulDataTypeInstanceSchema).min(1).required(),
        }),
        joi.object({
          not: joi.array().items(ResourcefulDataTypeInstanceSchema).min(1).required(),
        })
      )
      .required(),
    minItems: joi.number().min(0).default(0).optional(),
    maxItems: joi.number().min(0).default(Number.MAX_SAFE_INTEGER).optional(),
    uniqueItems: joi.boolean().default(false).optional(),
    ...CommonModifiersSchema,
  })
